import React, { useState, useRef, useEffect } from "react";
import { focusRowUnderPointer } from "./menuFocus";
import {
  KEYS,
  handleListKeyboardNavigation,
  generateId,
} from "../../utils/accessibility";

/**
 * Accessible Dropdown Component
 *
 * WCAG compliant dropdown with keyboard navigation and ARIA attributes
 */
const Dropdown = ({ children, className = "" }) => {
  return (
    <div className={`relative inline-block text-left ${className}`}>
      {children}
    </div>
  );
};

const DropdownContext = React.createContext({});

const Trigger = ({ children, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const menuId = useRef(generateId("dropdown-menu"));
  const triggerId = useRef(generateId("dropdown-trigger"));

  const handleClick = () => {
    setOpen(!open);
    if (!open) {
      setSelectedIndex(-1);
    }
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case KEYS.ENTER:
      case KEYS.SPACE:
        e.preventDefault();
        handleClick();
        break;
      case KEYS.ARROW_DOWN:
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setSelectedIndex(0);
        }
        break;
      case KEYS.ESCAPE:
        if (open) {
          e.preventDefault();
          setOpen(false);
          triggerRef.current?.focus();
        }
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        open &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        contentRef.current &&
        !contentRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Create context value
  const contextValue = {
    open,
    setOpen,
    selectedIndex,
    setSelectedIndex,
    menuId: menuId.current,
    triggerId: triggerId.current,
    triggerRef,
    contentRef,
  };

  // Clone children and add props
  const triggerElement = React.Children.only(children);
  const enhancedTrigger = React.cloneElement(triggerElement, {
    ref: triggerRef,
    id: triggerId.current,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    "aria-haspopup": "true",
    "aria-expanded": open,
    "aria-controls": open ? menuId.current : undefined,
    className: `${triggerElement.props.className || ""} ${className}`,
    role: "button",
    tabIndex: triggerElement.props.tabIndex ?? 0,
  });

  return (
    <DropdownContext.Provider value={contextValue}>
      {enhancedTrigger}
      {open && <DropdownContent />}
    </DropdownContext.Provider>
  );
};

const DropdownContent = () => {
  const context = React.useContext(DropdownContext);
  const {
    menuId,
    selectedIndex,
    setSelectedIndex,
    setOpen,
    triggerRef,
    contentRef,
  } = context;
  const itemRefs = useRef([]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    const items = itemRefs.current.filter(Boolean);
    const currentIndex = selectedIndex;

    switch (e.key) {
      case KEYS.ARROW_DOWN:
        e.preventDefault();
        const nextIndex =
          currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        setSelectedIndex(nextIndex);
        items[nextIndex]?.focus();
        break;
      case KEYS.ARROW_UP:
        e.preventDefault();
        const prevIndex =
          currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        setSelectedIndex(prevIndex);
        items[prevIndex]?.focus();
        break;
      case KEYS.HOME:
        e.preventDefault();
        setSelectedIndex(0);
        items[0]?.focus();
        break;
      case KEYS.END:
        e.preventDefault();
        const lastIndex = items.length - 1;
        setSelectedIndex(lastIndex);
        items[lastIndex]?.focus();
        break;
      case KEYS.ESCAPE:
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      case KEYS.TAB:
        // Allow tab to close dropdown
        setOpen(false);
        break;
    }
  };

  // Focus first item when opened with keyboard
  useEffect(() => {
    if (selectedIndex === 0 && itemRefs.current[0]) {
      itemRefs.current[0].focus();
    }
  }, [selectedIndex]);

  return (
    <div
      ref={contentRef}
      id={menuId}
      role="menu"
      aria-orientation="vertical"
      onKeyDown={handleKeyDown}
      onMouseMove={focusRowUnderPointer()}
      className="sg-menu absolute z-30 mt-2 w-56 origin-top-right focus:outline-none"
    >
      <DropdownContext.Provider value={{ ...context, itemRefs }}>
        {React.Children.toArray(Dropdown.Content?.children).map(
          (child, index) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                index,
                onClose: () => setOpen(false),
              });
            }
            return child;
          },
        )}
      </DropdownContext.Provider>
    </div>
  );
};

const Content = ({ children, align = "right", className = "", ...props }) => {
  Content.children = children;

  const alignClasses = {
    left: "left-0 origin-top-left",
    right: "right-0 origin-top-right",
    start: "left-0 origin-top-left",
    end: "right-0 origin-top-right",
  };

  return null;
};

const Item = ({
  children,
  onSelect,
  index,
  className = "",
  disabled = false,
  ...props
}) => {
  const context = React.useContext(DropdownContext);
  const { itemRefs, selectedIndex, setOpen } = context;

  const handleClick = () => {
    if (!disabled && onSelect) {
      onSelect();
      setOpen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === KEYS.ENTER || e.key === KEYS.SPACE) {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      ref={(el) => (itemRefs.current[index] = el)}
      role="menuitem"
      tabIndex={-1}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={`sg-menu-item ${className}`}
      data-selected={selectedIndex === index ? "true" : undefined}
      {...props}
    >
      {children}
    </button>
  );
};

const Separator = ({ className = "" }) => {
  return (
    <div
      role="separator"
      className={`h-px my-1 bg-sg-line ${className}`}
    />
  );
};

// Attach components to Dropdown
Dropdown.Trigger = Trigger;
Dropdown.Content = Content;
Dropdown.Item = Item;
Dropdown.Separator = Separator;

export default Dropdown;
